/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import Modal from './modal/modal';
import Typography from './typography/typography';
import Button from './button/button';
import ModalLoader from './loaders/modal-loader';
import { DynamicInput, IModalComponentProps } from './form-control';

interface IndustryFormValues {
	industry: {
		name: string;
	};
}

interface IndustryAddModalProps extends IModalComponentProps {
	type?: string;
}

export const IndustryAddModal: React.FC<IndustryAddModalProps> = ({
	isOpen,
	setIsOpen,
	onSave,
	isLoading,
	modalProps,
}) => {
	const { t } = useTranslation();

	const validationSchema = Yup.object().shape({
		industry: Yup.object().shape({
			name: Yup.string()
				.required('Industry name is required')
				.max(255, 'Industry name must be at most 255 characters'),
		}),
	});

	const { control, handleSubmit, reset } = useForm<IndustryFormValues>({
		resolver: yupResolver(validationSchema),
		mode: 'all',
		defaultValues: {
			industry: {
				name: '',
			},
		},
	});

	const handleClose = () => {
		reset({
			industry: {
				name: '',
			},
		});
		setIsOpen(false);
	};

	const handleFormSubmit = (event?: React.BaseSyntheticEvent) => {
		event?.preventDefault();
		event?.stopPropagation();

		return handleSubmit(onSubmit)(event);
	};

	const onSubmit = async (data: IndustryFormValues) => {
		try {
			await onSave(data.industry as any);
			handleClose();
		} catch (error) {
			console.error('❌ ~ Error saving industry:', error);
		}
	};

	return (
		<Modal
			open={isOpen}
			onClose={handleClose}
			maxWidth={modalProps?.maxWidth || 'sm'}
			fullWidth={modalProps?.fullWidth || true}
			className="industry-add-modal"
		>
			<DialogTitle>
				<Typography type="h5" weight="medium" color="theme.secondary.1000">
					{t('crm.leads.fields.create_industry_label', 'Create New Industry')}
				</Typography>
				<IconButton onClick={handleClose}>
					<Close fontSize='small' />
				</IconButton>
			</DialogTitle>

			<form onSubmit={handleFormSubmit}>
				<DialogContent>
					{isLoading ? (
						<ModalLoader />
					) : (
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<DynamicInput
									label={t('crm.leads.fields.industry_name_label', 'Industry Name')}
									name="name"
									placeholder={t('crm.leads.fields.industry_name_placeholder', 'Enter Industry Name')}
									fieldArrayName="industry"
									formControl={control}
									required
								/>
							</Grid>
						</Grid>
					)}
				</DialogContent>

				<DialogActions>
					<Button
						type="button"
						variant="outlined"
						onClick={handleClose}
						disabled={isLoading}
					>
						{t('common.cancel', 'Cancel')}
					</Button>
					<Button
						type="submit"
						variant="contained"
						disabled={isLoading}
					>
						{t('common.save', 'Save')}
					</Button>
				</DialogActions>
			</form>
		</Modal>
	);
};
